import { Fragment, type ReactNode } from 'react';

/**
 * markdownLite — a tiny, XSS-safe subset of markdown for Roldi's output.
 *
 * Supports:
 *   - **bold**
 *   - *italic* (single-asterisk, not inside words)
 *   - `inline code`
 *   - hard line breaks (preserved from the source)
 *   - pipe-style tables → fixed-width <pre>
 *   - # … #### headings → bold block lines (models emit them constantly;
 *     they used to print as literal `###` — elite-audit P2, 2026-08-26)
 *   - --- / *** horizontal rules → a thin border line
 *
 * Everything else passes through as plain text. We never set innerHTML; we
 * only emit React nodes so the output is safe against injection.
 */
/**
 * Drop a trailing emphasis marker that never got its closing partner.
 *
 * A reply cut off by the model's output cap — or still mid-stream — can end
 * on a half-written `**Unit 0374`, and the renderer faithfully painted a
 * literal `**` into the last table cell (closing trace, 2026-08-26: a manager
 * saw `| 🔴 #2 | ** |`). Truncation is upstream and stays a real problem; this
 * just makes it degrade to plain text instead of showing punctuation debris.
 * Only an ODD (unclosed) count is touched, and only at the very end.
 */
function dropDanglingEmphasis(src: string): string {
  let out = src;
  for (const marker of ['**', '__']) {
    const count = out.split(marker).length - 1;
    if (count % 2 === 1) {
      const at = out.lastIndexOf(marker);
      out = out.slice(0, at) + out.slice(at + marker.length);
    }
  }
  return out;
}

export function renderMarkdownLite(rawSrc: string): ReactNode {
  const src = dropDanglingEmphasis(rawSrc);
  const lines = src.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    // Detect a pipe table: at least 2 consecutive lines where both contain |
    // and the second line is a separator row like |---|---|
    const next = lines[i + 1] ?? '';
    if (line.includes('|') && /^\s*\|?\s*:?-{2,}/.test(next) && next.includes('|')) {
      const tableLines: string[] = [];
      while (i < lines.length) {
        const l = lines[i] ?? '';
        if (l.trim() === '') break;
        if (!l.includes('|')) break;
        tableLines.push(l);
        i++;
      }
      blocks.push(renderPipeTable(tableLines, `tbl-${blocks.length}`));
      continue;
    }

    // Heading: 1–4 hashes then whitespace. `#6614` (a truck number) has no
    // space after the hash and stays plain text.
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(
        <strong key={`h-${i}`} className="mt-1.5 block first:mt-0">
          {renderInline(heading[2] ?? '')}
        </strong>,
      );
      i++;
      continue;
    }

    // Horizontal rule: a line of only --- / *** / ___ (3+). Pipe-table
    // separators never reach here — the table branch above consumes them.
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(<span key={`hr-${i}`} className="border-fw-border/60 my-1 block border-t" />);
      i++;
      continue;
    }

    blocks.push(
      <Fragment key={`ln-${i}`}>
        {renderInline(line)}
        {i < lines.length - 1 ? '\n' : null}
      </Fragment>,
    );
    i++;
  }
  return <>{blocks}</>;
}

function renderInline(text: string): ReactNode {
  if (text.length === 0) return null;

  // First pass: extract backtick code spans as inert atoms. Subsequent
  // bold/italic matching runs over the text between atoms so asterisks
  // adjacent to (or inside) code can't accidentally open an italic.
  const atoms = tokenizeCode(text);
  const parts: ReactNode[] = [];
  let key = 0;

  for (const atom of atoms) {
    if (atom.type === 'code') {
      parts.push(
        <code
          key={`c${key++}`}
          className="bg-fw-bg border-fw-border rounded border px-1 py-0.5 font-mono text-[0.875em]"
        >
          {atom.content}
        </code>,
      );
      continue;
    }
    // Text atom — resolve bold and italic.
    parts.push(...renderBoldItalic(atom.content, () => `bi${key++}`));
  }

  return <>{parts}</>;
}

type InlineAtom = { type: 'text'; content: string } | { type: 'code'; content: string };

/** Split a line into alternating text / code atoms based on balanced backticks. */
function tokenizeCode(src: string): InlineAtom[] {
  const out: InlineAtom[] = [];
  let i = 0;
  let buf = '';
  while (i < src.length) {
    if (src[i] === '`') {
      const end = src.indexOf('`', i + 1);
      if (end === -1) {
        // Unterminated tick — treat the rest as plain text.
        buf += src.slice(i);
        i = src.length;
        break;
      }
      if (end === i + 1) {
        // Empty backtick pair — render as literal text.
        buf += '``';
        i = end + 1;
        continue;
      }
      if (buf) {
        out.push({ type: 'text', content: buf });
        buf = '';
      }
      out.push({ type: 'code', content: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    buf += src[i];
    i++;
  }
  if (buf) out.push({ type: 'text', content: buf });
  return out;
}

function renderBoldItalic(text: string, nextKey: () => string): ReactNode[] {
  if (text.length === 0) return [];
  const parts: ReactNode[] = [];
  let rest = text;

  while (rest.length > 0) {
    const bold = findDelim(rest, '**', '**');
    const ital = findDelimItalic(rest);
    const firstIdx = Math.min(bold ? bold.openIdx : Infinity, ital ? ital.openIdx : Infinity);
    if (!isFinite(firstIdx)) {
      parts.push(rest);
      break;
    }
    if (firstIdx > 0) parts.push(rest.slice(0, firstIdx));

    if (bold && bold.openIdx === firstIdx) {
      parts.push(
        <strong key={nextKey()} className="font-semibold">
          {renderInline(bold.inner)}
        </strong>,
      );
      rest = rest.slice(bold.closeIdxEnd);
      continue;
    }
    if (ital && ital.openIdx === firstIdx) {
      parts.push(
        <em key={nextKey()} className="italic">
          {renderInline(ital.inner)}
        </em>,
      );
      rest = rest.slice(ital.closeIdxEnd);
      continue;
    }
    parts.push(rest);
    break;
  }
  return parts;
}

interface Delim {
  openIdx: number;
  closeIdxEnd: number;
  inner: string;
}

function findDelim(src: string, open: string, close: string): Delim | null {
  const openIdx = src.indexOf(open);
  if (openIdx === -1) return null;
  const afterOpen = openIdx + open.length;
  const closeIdx = src.indexOf(close, afterOpen);
  if (closeIdx === -1 || closeIdx === afterOpen) return null;
  return {
    openIdx,
    closeIdxEnd: closeIdx + close.length,
    inner: src.slice(afterOpen, closeIdx),
  };
}

/**
 * Render a GFM-style pipe table as a real <table>. Header row = first line,
 * separator row = second line, body rows = anything after. Each cell goes
 * back through `renderInline` so bold / italic / code work inside cells.
 * Cells can still contain `|` via backslash-escape (`\|`).
 */
function renderPipeTable(lines: string[], key: string): ReactNode {
  if (lines.length < 2) return null;
  const [headerLine, , ...bodyLines] = lines;
  if (!headerLine) return null;

  const header = splitCells(headerLine);
  const body = bodyLines.map((ln) => splitCells(ln));

  return (
    <div
      key={key}
      className="border-fw-border bg-fw-bg-raised my-2 overflow-x-auto rounded-lg border"
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-fw-border/60 bg-fw-surface border-b">
            {header.map((cell, idx) => (
              <th
                key={idx}
                className="text-fw-text-muted px-3 py-2 text-left text-[11px] font-semibold tracking-wide uppercase"
              >
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r} className="border-fw-border/40 border-b last:border-b-0">
              {row.map((cell, c) => (
                <td key={c} className="text-fw-text px-3 py-2 align-top">
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function splitCells(line: string): string[] {
  // Strip one leading / trailing pipe, respect backslash-escaped pipes.
  const trimmed = line.replace(/^\s*\|/, '').replace(/\|\s*$/, '');
  const cells: string[] = [];
  let buf = '';
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === '\\' && trimmed[i + 1] === '|') {
      buf += '|';
      i++;
      continue;
    }
    if (ch === '|') {
      cells.push(buf.trim());
      buf = '';
      continue;
    }
    buf += ch;
  }
  cells.push(buf.trim());
  return cells;
}

/** Italic: single `*` not adjacent to another `*`. Avoids colliding with bold. */
function findDelimItalic(src: string): Delim | null {
  for (let i = 0; i < src.length; i++) {
    if (src[i] !== '*') continue;
    if (src[i - 1] === '*' || src[i + 1] === '*') continue;
    // Find the matching close
    for (let j = i + 1; j < src.length; j++) {
      if (src[j] !== '*') continue;
      if (src[j - 1] === '*' || src[j + 1] === '*') continue;
      if (j === i + 1) break;
      return { openIdx: i, closeIdxEnd: j + 1, inner: src.slice(i + 1, j) };
    }
    return null;
  }
  return null;
}
